import { jsPDF } from 'jspdf';
import autoTable from 'jspdf-autotable';
import 'jspdf-font';
import { Quotation } from '../../types/quotation';
import { Organization } from '../../types/organization';
import { Customer } from '../../types/customer';
import { Product } from '../../types/product';
import { format } from 'date-fns';
import { formatAddress } from '../../utils/formatters';

export const generateHungarianPdf = (
  doc: jsPDF,
  quotation: Quotation,
  organization?: Organization,
  customer?: Customer,
  products?: Product[]
) => {
  const pdfDoc = new jsPDF('p', 'mm', 'a4');
  const margin = 20;
  let y = margin;

  // Oldalszámozás hozzáadása
  const addPageNumber = (pageNum: number, totalPages: number) => {
    const pageSize = pdfDoc.internal.pageSize;
    const pageWidth = pageSize.width ? pageSize.width : pageSize.getWidth();
    pdfDoc.setFontSize(8);
    pdfDoc.text(`${pageNum} / ${totalPages}`, pageWidth - margin, pageSize.height - margin, { align: 'right' });
  };

  // Fejléc
  const addHeader = () => {
    y = margin;
    if (organization) {
      // Bal oldali információk
      pdfDoc.setFontSize(16);
      pdfDoc.setTextColor(0, 0, 0);
      pdfDoc.text(organization.name, margin, y);
      y += 8;

      pdfDoc.setFontSize(10);
      pdfDoc.setTextColor(100, 100, 100);  // szürke szín
      pdfDoc.text(formatAddress(organization.address), margin, y);
      y += 5;
      if (organization.taxNumber) {
        pdfDoc.text(`Adószám: ${organization.taxNumber}`, margin, y);
        y += 10;
      }

      // Jobb oldali információk (árajánlat adatok)
      const pageWidth = pdfDoc.internal.pageSize.width;
      const rightMargin = pageWidth - margin;

      // Árajánlat szám nagyobb méretben és primary színnel
      pdfDoc.setFontSize(20);
      pdfDoc.setTextColor(79, 70, 229);  // primary szín
      pdfDoc.text(quotation.quotationNumber || quotation.number, rightMargin, margin + 8, { align: 'right' });

      // Dátumok kisebb méretben és szürke színnel
      pdfDoc.setFontSize(9);
      pdfDoc.setTextColor(100, 100, 100);
      pdfDoc.text(`Létrehozva: ${format(quotation.createdAt, 'yyyy. MMMM d.')}`, rightMargin, margin + 16, { align: 'right' });
      pdfDoc.text(`Érvényes: ${quotation.validUntil ? format(quotation.validUntil, 'yyyy. MMMM d.') : 'N/A'}`, rightMargin, margin + 22, { align: 'right' });

      // Vízszintes vonal
      y = margin + 35;
      pdfDoc.setDrawColor(220, 220, 220);
      pdfDoc.setLineWidth(0.5);
      pdfDoc.line(margin, y, pageWidth - margin, y);
      y += 15;
    }

    // Visszaállítjuk az alapértelmezett színeket
    pdfDoc.setTextColor(0, 0, 0);
  };

  // Első oldal fejléce
  addHeader();

  // Ügyfél információk
  if (customer) {
    pdfDoc.setFontSize(10);
    pdfDoc.setTextColor(100, 100, 100);
    pdfDoc.text('Címzett:', margin, y);
    y += 5;

    const customerName = customer.type === 'organization'
      ? (customer.companyName || 'N/A')
      : `${customer.firstName || ''} ${customer.lastName || ''}`.trim() || 'N/A';

    pdfDoc.setTextColor(0, 0, 0);
    pdfDoc.setFontSize(11);
    pdfDoc.text(customerName, margin, y);
    y += 5;

    pdfDoc.setFontSize(10);
    pdfDoc.setTextColor(100, 100, 100);
    pdfDoc.text(formatAddress(customer.address), margin, y);
    if (customer.taxNumber) {
      y += 5;
      pdfDoc.text(`Adószám: ${customer.taxNumber}`, margin, y);
    }
    y += 15;
  }

  // Visszaállítjuk az alapértelmezett színeket a további tartalomhoz
  pdfDoc.setTextColor(0, 0, 0);

  // Tételek táblázat
  autoTable(pdfDoc, {
    head: [['Megnevezes', 'Mennyiseg', 'Egysegar', 'AFA', 'Osszesen']],
    body: quotation.items.map(item => {
      const product = products?.find(p => p.id === item.productId);
      const itemTotal = item.price * item.quantity;
      const itemTotalWithTax = itemTotal * (1 + item.taxRate / 100);

      return [
        product?.name || '',
        item.quantity.toString(),
        `${item.price.toLocaleString()} ${item.currency}`,
        `${item.taxRate}%`,
        `${itemTotal.toLocaleString()} ${item.currency}\nAFA-val: ${itemTotalWithTax.toLocaleString()} ${item.currency}`
      ];
    }),
    startY: y,
    // Itt kell a fejléc utáni extra helyet adni
    margin: { left: margin, right: margin, top: margin + 50, bottom: margin },
    styles: {
      font: 'helvetica',
      cellPadding: 4,
      fontSize: 9,
      overflow: 'linebreak',
      minCellHeight: 10,
      cellWidth: 'wrap'
    },
    columnStyles: {
      0: { cellWidth: 'auto', minCellWidth: 65 },
      1: { cellWidth: 20, halign: 'right' },
      2: { cellWidth: 30, halign: 'right' },
      3: { cellWidth: 15, halign: 'right' },
      4: { cellWidth: 40, halign: 'right' }
    },
    headStyles: {
      fillColor: [200, 200, 200],
      textColor: [0, 0, 0],
      fontSize: 9
    },
    bodyStyles: {
      minCellHeight: 10
    },
    didDrawPage: (data) => {
      // Új oldal esetén fejléc hozzáadása
      if (data.pageNumber > 1) {
        addHeader();
        // A fejléc után hagyjunk extra helyet
        // if (data.cursor) {
        //   data.cursor.y = margin + 55;
        // }
      }
      y = data.cursor?.y ?? y;
    },
    willDrawCell: (data) => {
      // Ha a cella tartalma túl hosszú, automatikusan tördelje
      const cellText = data.cell.text as string | string[];
      const textContent = Array.isArray(cellText) ? cellText.join('') : cellText;
      if (textContent && textContent.length > 50) {
        data.cell.styles.cellWidth = 'wrap';
      }
    },
    showFoot: 'lastPage'
  });

  // Összesítés
  y += 15;
  pdfDoc.setFontSize(9);

  // Ellenőrizzük, hogy van-e elég hely az összesítéshez, ha nincs, új oldalt kezdünk
  if (y > pdfDoc.internal.pageSize.height - 60) {
    pdfDoc.addPage();
    addHeader();
    y = margin + 50;
  }

  const rightColumnX = margin + 170;
  const leftColumnX = rightColumnX - 50;

  const total = quotation.items.reduce((sum, item) => sum + (item.price * item.quantity), 0);
  const totalWithTax = quotation.items.reduce((sum, item) =>
    sum + (item.price * item.quantity * (1 + item.taxRate / 100)), 0
  );

  pdfDoc.text('Reszosszeg:', leftColumnX, y);
  pdfDoc.text(`${total.toLocaleString()} ${quotation.items[0]?.currency}`, rightColumnX, y, { align: 'right' });
  y += 5;
  pdfDoc.text('Vegosszeg AFA-val:', leftColumnX, y);
  pdfDoc.text(`${totalWithTax.toLocaleString()} ${quotation.items[0]?.currency}`, rightColumnX, y, { align: 'right' });

  // Megjegyzések
  if (quotation.notes) {
    y += 15;

    // Ellenőrizzük, hogy van-e elég hely a megjegyzésekhez
    if (y > pdfDoc.internal.pageSize.height - 40) {
      pdfDoc.addPage();
      addHeader();
      y = margin + 30;
    }

    pdfDoc.text('Megjegyzesek:', margin, y);
    y += 5;
    const lines = pdfDoc.splitTextToSize(quotation.notes, pdfDoc.internal.pageSize.width - (2 * margin));
    lines.forEach((line: string) => {
      if (y > pdfDoc.internal.pageSize.height - 40) {
        pdfDoc.addPage();
        addHeader();
        y = margin + 30;
      }
      pdfDoc.text(line, margin, y);
      y += 5;
    });
  }

  // Lábléc
  y += 15;

  // Ellenőrizzük, hogy van-e elég hely a láblécnek
  if (y > pdfDoc.internal.pageSize.height - 60) {
    pdfDoc.addPage();
    addHeader();
    y = margin + 50;
  }

  pdfDoc.text('Feltetelek:', margin, y);
  y += 5;
  [
    '- Az arajanlat a fent megjelolt idopontig ervenyes.',
    '- Az arak elozetes ertesites nelkul valtozhatnak.',
    '- Fizetesi feltetelek: 8 nap a szamla kiallitasatol.',
    '- Az arajanlat alairas es pecset nelkul is ervenyes.'
  ].forEach(line => {
    if (y > pdfDoc.internal.pageSize.height - 40) {
      pdfDoc.addPage();
      addHeader();
      y = margin + 30;
    }
    pdfDoc.text(line, margin, y);
    y += 5;
  });

  y += 10;
  if (y > pdfDoc.internal.pageSize.height - 20) {
    pdfDoc.addPage();
    addHeader();
    y = margin + 30;
  }
  pdfDoc.setFontSize(8);
  pdfDoc.text('Keszult a sajat fejlesztesu Energia Info CRM rendszerrel', 105, y, { align: 'center' });

  // Oldalszámok hozzáadása minden oldalhoz
  const totalPages = pdfDoc.internal.pages.length - 1; // Az utolsó üres oldalt ne számoljuk
  for (let i = 1; i <= totalPages; i++) {
    pdfDoc.setPage(i);
    addPageNumber(i, totalPages);
  }

  return pdfDoc;
}; 